/** @jsxImportSource @emotion/react */
import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Button, Tab, Tabs } from '@mui/material';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useMessageBar } from 'hooks/useMessageBar';
import { Code } from 'models/common/CommonCode';
import { useCommonModal } from 'hooks/useCommonModal';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { useLoading } from 'components/process/Loading';
import { MdIvesCondition, MdIvesPrjInfo, TabMenu } from 'models/md/MdIves';
import { useNavigate } from 'react-router-dom';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { tabs } from 'components/layouts/Tab';
import IvesAtc from './atcMgt/IvesAtc';
import DtlIsptMatr from './atcMgt/DtlIsptMatr';
import CustomGrid from 'components/grids/CustomGrid';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { downloadExcelTemplatesPost } from 'apis/common/Excel';
import { FileTypeName } from 'models/common/Common';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { CommonUtil } from 'utils/CommonUtil';

/**
 * 생산라인 목록 페이지
 */
const MdPrdnLnInqPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const navigate = useNavigate();

  const initCondition = {
    wbs: '',
  };

  const gridRef = useRef();
  const [bildPlntCodes, setBildPlntCodes] = useState<Code[]>([]);
  const [bldoCodes, setBldoCodes] = useState<Code[]>([]);
  const [condition, setCondition] = useState<any>({ ...initCondition });
  const [rowData, setRowData] = useState<any>([]);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        align: 'center',
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.동(빌딩) 코드', '동(빌딩) 코드')),
        width: 200,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.동(빌딩) 명', '동(빌딩) 명')),
        width: '*',
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.수정자', '수정자')),
        width: 100,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.수정일시', '수정일시')),
        width: 140,
        align: 'center',
        dataType: 'Number',
      },
    ];
  }, [rowData]);

  const initCodes = async () => {
    const bildPlntCode = await getCommonCodeNames('BILD_PLNT_CD');
    setBildPlntCodes(bildPlntCode);
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const btnSearch = () => {
    // search
  };

  const btnRegist = () => {
    //
  };

  const btnModify = () => {
    //
  };

  const btnDelete = () => {
    //
  };

  const btnAdd = async () => {
    //
  };

  const btnDel = () => {
    //
  };

  const handleDownloadExcel = () => {
    // excel download
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.생산라인 목록', '생산라인 목록'),
      t('setup.label.생산라인 목록', '생산라인 목록')
    );
  };

  useEffect(() => {
    initCodes();
    btnSearch();
  }, []);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('setup.label.플랜트', '플랜트'))}</label>
                <ComboBox
                  options={bildPlntCodes}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition.bildPlntCd}
                  msgError={String(t('com.msg.플랜트를 선택해 주세요.', '플랜트를 선택해 주세요.'))}
                  onChange={(value) => handleOnChange('bildPlntCd', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('setup.label.동', '동'))}</label>
                <ComboBox
                  options={bldoCodes}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition.bldoCd}
                  msgError={String(t('com.msg.동을 선택해 주세요.', '동을 선택해 주세요.'))}
                  onChange={(value) => handleOnChange('bldoCd', value)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setCondition}
              onSearch={() => btnSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.생산라인 목록', '생산라인 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button css={IconButton.button} className="addRow" onClick={btnAdd} disableRipple>
            {t('com.button.행추가', '행추가')}
          </Button>
          <Button css={IconButton.button} className="delRow" onClick={btnDel} disableRipple>
            {t('com.button.행삭제', '행삭제')}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => btnSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid layoutDefinition={layoutDefinition} rowData={rowData} height={500} />
      <GlobalBtnGroup>
        <Button
          css={IconButton.button}
          className="icoNone ok"
          onClick={() => btnRegist()}
          disableRipple
        >
          {t('com.button.저장', '저장')}
        </Button>
      </GlobalBtnGroup>
    </>
  );
};

export default MdPrdnLnInqPage;
